import { useEffect, useState, useRef } from "react";
import _ from "lodash";
import uiSetting from "../../../apis/uiSetting";
import { getGroupColumnData } from "@/cleanup/utils/tableUtils";

export const useUiSetting = (pageName, fixedSetting) => {
  const defaultMapSettings = {
    selectedLayers: [],
    mapLayer: "",
    geozoneLayer: false,
    networkLayer: false,
    assetLayer: false,
    infraLayer: false,
    siteMapLayer: true,
    layers: [],
  };
  const [setting, setSetting] = useState({});
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    if (!pageName) {
      setSetting({
        useColumnFilter: true,
        groups: fixedSetting ? fixedSetting.groups : [],
        orderData: fixedSetting ? fixedSetting.orderData : null,
        columnVisibleInOrder: fixedSetting
          ? fixedSetting.columnVisibleInOrder
          : [],
        pageSize: fixedSetting ? fixedSetting.pageSize : 25,
        pageIndex: fixedSetting ? fixedSetting.pageIndex : 0,
        mapSettings: defaultMapSettings,
      });
      return;
    }

    uiSetting
      .findOne(pageName)
      .then(({ data }) => {
        if (!isMountedRef.current) return;
        if (data.mapSettings) {
          data.mapSettings = JSON.parse(data.mapSettings);
        }

        // If fixedSetting is provided, use it instead of loaded data (override mode)
        if (fixedSetting) {
          setSetting({
            useColumnFilter: true,
            ...fixedSetting,
            mapSettings: data.mapSettings || defaultMapSettings,
          });
        } else {
          setSetting(data);
        }
      })
      .catch((err) => {
        if (!isMountedRef.current) return;
        // in useColumnFilter using setting is not empty
        const fallbackSetting = {
          useColumnFilter: true,
          groups: fixedSetting ? fixedSetting.groups : [],
          orderData: fixedSetting ? fixedSetting.orderData : null,
          columnVisibleInOrder: fixedSetting
            ? fixedSetting.columnVisibleInOrder
            : [],
          pageSize: fixedSetting ? fixedSetting.pageSize : 25,
          pageIndex: fixedSetting ? fixedSetting.pageIndex : 0,
          mapSettings: pageName === "DASHBOARD_MAP" ? {} : defaultMapSettings,
        };
        setSetting(fallbackSetting);
      });

    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateSetting = (filter) => {
    if (!pageName) return;

    let { groups } = getGroupColumnData(filter.columns);
    let nextSetting = {
      columnVisibleInOrder: filter.columns
        .filter((c) => !c.immovable)
        .map((c) => c.columnKey),
      orderData: [
        filter.sortBy.column.sortKey,
        filter.sortBy.desc ? "desc" : "asc",
      ],
      pageSize: filter.pageSize,
      pageIndex: filter.pageIndex,
      pageName: pageName,
      groups: groups.map((item) => {
        return {
          aggregateColumn: item.columnGroupKey, // uiSetting store aggregateColumn = columnKey
          aggregationType: item.aggregationType,
          bucketType: item.bucketType,
          bucketConfig: item.bucketConfig,
        };
      }),
    };
    if (
      !_.isEqual(
        nextSetting.columnVisibleInOrder,
        setting.columnVisibleInOrder
      ) ||
      !_.isEqual(nextSetting.orderData, setting.orderData) ||
      !_.isEqual(nextSetting.pageSize, setting.pageSize) ||
      !_.isEqual(nextSetting.pageIndex, setting.pageIndex) ||
      !_.isEqual(nextSetting.groups, setting.groups)
    ) {
      setSetting(nextSetting);
      uiSetting.updateSetting(nextSetting).then(
        () => {},
        (err) => {}
      );
    }
  };

  const updateDashboardMapSetting = (data) => {
    if (!pageName || !data) return;

    let nextSetting = {
      pageName: pageName,
      mapSettings: { ...data },
    };
    setSetting(nextSetting);
    if (!_.isEqual(data, setting.mapSettings)) {
      uiSetting
        .updateSetting({
          ...nextSetting,
          mapSettings: JSON.stringify(nextSetting.mapSettings),
        })
        .then(
          () => {},
          (err) => {}
        );
    }
  };

  const updateMapSetting = (data) => {
    if (!pageName || !data) return;

    let nextSetting = {
      pageName: pageName,
      mapSettings: { ...setting.mapSettings, ...data },
    };
    setSetting(nextSetting);

    if (
      !_.isEqual(data.selectedLayers, setting.mapSettings.selectedLayers) ||
      !_.isEqual(data.mapLayer, setting.mapSettings.mapLayer) ||
      !_.isEqual(data.geozoneLayer, setting.mapSettings.geozoneLayer) ||
      !_.isEqual(data.networkLayer, setting.mapSettings.networkLayer) ||
      !_.isEqual(data.assetLayer, setting.mapSettings.assetLayer) ||
      !_.isEqual(data.infraLayer, setting.mapSettings.infraLayer) ||
      !_.isEqual(data.siteMapLayer, setting.mapSettings.siteMapLayer) ||
      !_.isEqual(data.layers, setting.mapSettings.layers)
    ) {
      uiSetting
        .updateSetting({
          ...nextSetting,
          mapSettings: JSON.stringify(nextSetting.mapSettings),
        })
        .then(
          () => {},
          (err) => {}
        );
    }
  };

  return {
    setting,
    updateSetting,
    updateMapSetting,
    updateDashboardMapSetting,
  };
};
